import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ShieldCheck, Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function AuthModal({ open, onOpenChange }) {
  const { login } = useAuth();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("buyer");
  const [demoOtp, setDemoOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setStep("phone"); setPhone(""); setOtp(""); setName(""); setRole("buyer"); setDemoOtp("");
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const sendOtp = async () => {
    if (phone.replace(/\D/g, "").length !== 10) return toast.error("Enter a valid 10-digit number");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/send-otp", { phone });
      if (data.demo_otp) {
        setDemoOtp(data.demo_otp);
        toast.success(`Demo OTP is ${data.demo_otp}`);
      } else {
        setDemoOtp("");
        toast.success("OTP sent to your phone via SMS");
      }
      setStep("otp");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setLoading(false); }
  };

  const verify = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { phone, otp, name, role });
      login(data.token, data.user);
      toast.success("Logged in successfully");
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="auth-modal">
        <DialogHeader>
          <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-600 flex items-center justify-center mb-2">
            <ShieldCheck className="w-7 h-7 text-white" />
          </div>
          <DialogTitle className="text-center text-2xl font-heading">
            {step === "phone" ? "Login / Sign up" : "Verify OTP"}
          </DialogTitle>
          <p className="text-center text-sm text-slate-500">
            {step === "phone" ? "Continue with your mobile number" : `Enter the OTP sent to +91 ${phone}`}
          </p>
        </DialogHeader>

        {step === "phone" ? (
          <div className="space-y-4 pt-2">
            <button
              type="button"
              data-testid="auth-google-btn"
              onClick={googleLogin}
              className="w-full flex items-center justify-center gap-3 border border-slate-200 rounded-xl h-11 font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
              Continue with Google
            </button>
            <div className="flex items-center gap-3">
              <div className="h-px bg-slate-200 flex-1" />
              <span className="text-xs text-slate-400 uppercase tracking-wide">or use phone</span>
              <div className="h-px bg-slate-200 flex-1" />
            </div>
            <div>
              <Label className="mb-1.5 block">Mobile number</Label>
              <div className="flex items-center gap-2">
                <span className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 font-medium border">+91</span>
                <Input
                  data-testid="auth-phone-input"
                  inputMode="numeric" maxLength={10} placeholder="9876543210"
                  value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Your name (optional)</Label>
              <Input data-testid="auth-name-input" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label className="mb-2 block">I am a</Label>
              <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-3">
                {[["buyer", "Buyer / Tenant"], ["owner", "Owner / Seller"]].map(([v, l]) => (
                  <label key={v} className={`flex items-center gap-2 border rounded-xl px-3 py-2.5 cursor-pointer ${role === v ? "border-emerald-600 bg-emerald-50" : "border-slate-200"}`}>
                    <RadioGroupItem value={v} data-testid={`auth-role-${v}`} />
                    <span className="text-sm font-medium">{l}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <Button data-testid="auth-send-otp-btn" className="w-full bg-emerald-600 hover:bg-emerald-700 h-11" onClick={sendOtp} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Phone className="w-4 h-4 mr-2" /> Send OTP</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            {demoOtp && (
              <div className="text-center text-sm bg-amber-50 text-amber-800 border border-amber-200 rounded-lg py-2">
                Demo OTP: <span className="font-bold tracking-widest">{demoOtp}</span>
              </div>
            )}
            <Input
              data-testid="auth-otp-input"
              className="text-center text-2xl tracking-[0.5em] h-14 font-bold"
              inputMode="numeric" maxLength={6} placeholder="••••"
              value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            />
            <Button data-testid="auth-verify-btn" className="w-full bg-emerald-600 hover:bg-emerald-700 h-11" onClick={verify} disabled={loading || otp.length < 4}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Continue"}
            </Button>
            <button className="text-sm text-slate-500 w-full text-center hover:text-emerald-600" onClick={() => setStep("phone")}>
              Change number
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
